import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, uploadImage } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Upload, Bell, MapPin, Plus, Trash2 } from "lucide-react";

const EMPTY = { name: "", category: "plumbing", custom_category: "", description: "", city: "", state: "", phone: "", email: "", website: "", address: "", logo: "", cover_image: "", hourly_rate: 0 };
const DEFAULT_NOTIF = { emails_enabled: true, reminder_hour: 13 };

export default function BusinessSettings() {
  const { t, catLabel } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notif, setNotif] = useState(DEFAULT_NOTIF);
  const [savingNotif, setSavingNotif] = useState(false);
  const [history, setHistory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [addon, setAddon] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [savingLoc, setSavingLoc] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [payAlert, setPayAlert] = useState(null);

  const refreshBiz = () => api.get("/businesses/mine").then((rr) => { if (rr.data) { setAddon(rr.data.location_addon || null); setLocations(rr.data.locations || []); } }).catch(() => {});

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/businesses/mine").then((r) => { if (r.data) { setForm({ ...EMPTY, ...r.data }); if (r.data.notifications) setNotif({ ...DEFAULT_NOTIF, ...r.data.notifications }); setLocations(r.data.locations || []); setAddon(r.data.location_addon || null); } }).catch(() => {});
    api.get("/businesses/notifications/history").then((r) => setHistory(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPkgs(r.data.addons?.multi_location?.packages || [])).catch(() => {});
    const sid = new URLSearchParams(window.location.search).get("addon");
    if (sid && sid !== "cancel") {
      api.get(`/addons/multi-location/status/${sid}`).then((r) => {
        const ps = r.data.payment_status;
        if (ps === "paid") {
          toast.success("¡Complemento Multi-Sucursal activado!");
          refreshBiz();
        } else if (ps === "expired") {
          setPayAlert({ type: "error", title: "El pago no se completó", msg: "La sesión de pago expiró antes de finalizar. No se realizó ningún cobro. Puedes intentarlo de nuevo cuando quieras." });
        } else if (ps === "failed") {
          setPayAlert({ type: "error", title: "El pago falló", msg: "No pudimos procesar tu pago. No se realizó ningún cobro. Revisa tu método de pago e inténtalo de nuevo." });
        } else {
          setPayAlert({ type: "warn", title: "Pago pendiente", msg: "Aún no confirmamos tu pago. Si ya completaste el checkout, actualiza esta página en unos segundos." });
        }
        window.history.replaceState({}, "", "/dashboard/business");
      }).catch(() => {
        window.history.replaceState({}, "", "/dashboard/business");
      });
    } else if (sid === "cancel") {
      setPayAlert({ type: "warn", title: "Pago cancelado", msg: "Cancelaste el proceso de pago, así que el complemento no se activó. No se realizó ningún cobro." });
      window.history.replaceState({}, "", "/dashboard/business");
    }
  }, []);

  const saveNotif = async () => {
    setSavingNotif(true);
    try {
      await api.put("/businesses/notifications", { emails_enabled: notif.emails_enabled, reminder_hour: Number(notif.reminder_hour) });
      toast.success("Preferencias guardadas");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingNotif(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadLogo = (k) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      set(k, url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al subir la imagen");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/businesses", { ...form, hourly_rate: Number(form.hourly_rate) || 0 });
      toast.success("Negocio guardado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const selectPackage = async (size, billing) => {
    try {
      const r = await api.post("/addons/multi-location/select", { size, billing, origin_url: window.location.origin });
      if (r.data.activated) { setAddon(r.data.location_addon); toast.success("Complemento activado"); return; }
      if (r.data.checkout_url) { window.location.href = r.data.checkout_url; }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const addBranch = () => setLocations((prev) => [...prev, { name: "", address: "", city: "", state: "", phone: "" }]);
  const updateBranch = (i, k, v) => setLocations((prev) => prev.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));
  const removeBranch = (i) => setLocations((prev) => prev.filter((_, idx) => idx !== i));
  const cancelAddon = async () => {
    try {
      const r = await api.post("/addons/multi-location/cancel");
      if (r.data.scheduled) {
        setAddon(r.data.location_addon || null);
        toast.success("Cancelación programada al final de tu ciclo");
      } else {
        setAddon(null); setLocations([]);
        toast.success("Complemento cancelado");
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCancelOpen(false); }
  };
  const resumeAddon = async () => {
    try {
      const r = await api.post("/addons/multi-location/resume");
      setAddon(r.data.location_addon || null);
      toast.success("Complemento reactivado");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const saveBranches = async () => {    setSavingLoc(true);
    try {
      const r = await api.post("/businesses/locations", locations.filter((l) => (l.city || "").trim()));
      setLocations(r.data.locations || []);
      toast.success("Sucursales guardadas");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSavingLoc(false); }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{t("dash_profile")}</h1>
      <form onSubmit={save} className="mt-6 space-y-5 rounded-sm border border-slate-200 bg-white p-6" data-testid="business-form">
        <div className="flex items-center gap-4">
          <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-sm border border-slate-200 bg-slate-50">
            {form.logo ? <img src={form.logo} alt="logo" className="h-full w-full object-contain" /> : <Upload className="h-6 w-6 text-slate-300" />}
          </div>
          <label className="cursor-pointer rounded-sm border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-primary">
            Subir logo
            <input type="file" accept="image/*" onChange={uploadLogo("logo")} className="hidden" data-testid="logo-upload-input" />
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("name")}</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="biz-name-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("search_category")}</Label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className="mt-1.5 h-10 w-full rounded-sm border border-input bg-white px-3 text-sm" data-testid="biz-category-select">
              {categories.map((c) => <option key={c.key} value={c.key}>{catLabel(c)}</option>)}
            </select>
            {form.category === "other" && (
              <Input value={form.custom_category || ""} onChange={(e) => set("custom_category", e.target.value)} placeholder="Especifica tu categoría" className="mt-2 rounded-sm" data-testid="biz-custom-category-input" />
            )}
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("search_city")}</Label>
            <Input value={form.city} onChange={(e) => set("city", e.target.value)} required className="mt-1.5 rounded-sm" data-testid="biz-city-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("search_state")}</Label>
            <Input value={form.state} onChange={(e) => set("state", e.target.value)} required placeholder="TX, FL, CA..." className="mt-1.5 rounded-sm" data-testid="biz-state-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Teléfono</Label>
            <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 rounded-sm" data-testid="biz-phone-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("email")}</Label>
            <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-sm" data-testid="biz-email-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Sitio web</Label>
            <Input value={form.website} onChange={(e) => set("website", e.target.value)} className="mt-1.5 rounded-sm" data-testid="biz-website-input" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Tarifa por hora ($)</Label>
            <Input type="number" value={form.hourly_rate} onChange={(e) => set("hourly_rate", e.target.value)} className="mt-1.5 rounded-sm" data-testid="biz-rate-input" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Dirección</Label>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)} className="mt-1.5 rounded-sm" data-testid="biz-address-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Descripción</Label>
          <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="mt-1.5 rounded-sm" data-testid="biz-description-input" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Imagen de portada</Label>
          <div className="mt-1.5 overflow-hidden rounded-lg border border-slate-200 bg-slate-50" data-testid="cover-preview">
            {form.cover_image ? (
              <div className="relative aspect-[16/5] w-full max-w-xl">
                <img src={form.cover_image} alt="portada" className="h-full w-full object-cover" style={{ objectPosition: form.cover_position || "center" }} />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-secondary/70 to-transparent" />
                <span className="absolute bottom-2 left-3 rounded bg-black/40 px-2 py-0.5 text-xs font-semibold text-white">Vista previa: así se verá en tu perfil</span>
              </div>
            ) : (
              <div className="flex aspect-[16/5] w-full max-w-xl items-center justify-center text-slate-300"><Upload className="h-8 w-8" /></div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <label className="cursor-pointer rounded-sm border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:border-primary">
              {form.cover_image ? "Cambiar portada" : "Subir portada"}
              <input type="file" accept="image/*" onChange={uploadLogo("cover_image")} className="hidden" data-testid="cover-upload-input" />
            </label>
            {form.cover_image && <button type="button" onClick={() => set("cover_image", "")} className="text-xs font-semibold text-slate-400 hover:text-destructive" data-testid="cover-remove-btn">Quitar portada</button>}
          </div>
          {form.cover_image && (
            <div className="mt-3 max-w-xl">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Reposicionar (arrastra el control para elegir qué parte se ve)</label>
              <input type="range" min="0" max="100" value={parseFloat((form.cover_position || "50% 50%").split(" ")[1]) || 50} onChange={(e) => set("cover_position", `50% ${e.target.value}%`)} className="mt-1.5 w-full accent-primary" data-testid="cover-position-slider" />
            </div>
          )}
          <Input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="…o pega una URL (opcional)" className="mt-2 rounded-sm" data-testid="biz-cover-input" />
        </div>
        <Button type="submit" disabled={saving} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="biz-save-btn">{saving ? "…" : t("save")}</Button>
      </form>

      <div className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="notif-prefs">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><Bell className="h-5 w-5 text-primary" /> Notificaciones por email</h2>
        <p className="mt-1 text-sm text-slate-500">Recibe avisos de facturas pagadas, nuevas reseñas y recordatorios de entrega.</p>
        <div className="mt-4 flex items-center justify-between border-b border-slate-100 pb-4">
          <div>
            <p className="text-sm font-semibold text-secondary">Activar emails</p>
            <p className="text-xs text-slate-500">Enciende o apaga todas las notificaciones por correo.</p>
          </div>
          <Switch checked={notif.emails_enabled} onCheckedChange={(v) => setNotif((n) => ({ ...n, emails_enabled: v }))} data-testid="notif-enabled-switch" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-secondary">Hora del recordatorio diario</p>
            <p className="text-xs text-slate-500">Hora (UTC) en que recibirás el resumen de entregas.</p>
          </div>
          <select value={notif.reminder_hour} onChange={(e) => setNotif((n) => ({ ...n, reminder_hour: Number(e.target.value) }))} disabled={!notif.emails_enabled} className="h-9 rounded-sm border border-input bg-white px-2 text-sm disabled:opacity-50" data-testid="notif-hour-select">
            {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, "0")}:00 UTC</option>)}
          </select>
        </div>
        <Button onClick={saveNotif} disabled={savingNotif} className="mt-5 rounded-sm bg-secondary hover:bg-slate-700" data-testid="notif-save-btn">{savingNotif ? "…" : "Guardar preferencias"}</Button>

        <div className="mt-6 border-t border-slate-100 pt-4" data-testid="notif-history">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Historial de notificaciones</h3>
          <div className="mt-3 max-h-64 space-y-1.5 overflow-y-auto">
            {history.map((h) => (
              <div key={h.id} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm" data-testid={`notif-row-${h.id}`}>
                <div className="min-w-0">
                  <p className="truncate font-medium text-secondary">{h.subject}</p>
                  <p className="text-xs text-slate-400">{h.to} · {new Date(h.created_at).toLocaleString()}</p>
                </div>
                <span className={`ml-3 shrink-0 rounded-sm px-2 py-0.5 text-[10px] font-bold uppercase ${h.status === "sent" ? "bg-accent/10 text-accent" : h.status === "error" ? "bg-destructive/10 text-destructive" : "bg-slate-100 text-slate-500"}`}>
                  {h.status === "sent" ? "Enviado" : h.status === "error" ? "Error" : "Omitido"}
                </span>
              </div>
            ))}
            {history.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Aún no hay notificaciones.</p>}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="locations-section">
        <h2 className="flex items-center gap-2 font-head text-lg font-bold text-secondary"><MapPin className="h-5 w-5 text-primary" /> Sucursales / Ubicaciones</h2>
        <p className="mt-1 text-sm text-slate-500">Complemento Multi-Sucursal: tu negocio aparece en el directorio de cada ciudad donde tengas sucursal.</p>
        {payAlert && (
          <div className={`mt-4 flex items-start justify-between gap-3 rounded-sm border px-4 py-3 ${payAlert.type === "error" ? "border-destructive/30 bg-destructive/5" : "border-amber-300 bg-amber-50"}`} data-testid="addon-pay-alert">
            <div>
              <p className={`text-sm font-bold ${payAlert.type === "error" ? "text-destructive" : "text-amber-700"}`}>{payAlert.title}</p>
              <p className="mt-0.5 text-sm text-slate-600">{payAlert.msg}</p>
            </div>
            <button type="button" onClick={() => setPayAlert(null)} className="shrink-0 text-slate-400 hover:text-slate-600" data-testid="addon-pay-alert-dismiss">✕</button>
          </div>
        )}
        {!(addon && addon.active) ? (
          <div className="mt-4">
            <p className="text-sm font-semibold text-secondary">Elige un paquete para activar:</p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {pkgs.map((p) => (
                <div key={p.size} className="rounded-xl border border-slate-200 p-4 text-center" data-testid={`loc-pkg-${p.size}`}>
                  <p className="font-head text-xl font-extrabold text-secondary">{p.size}</p>
                  <p className="text-xs text-slate-400">sucursales</p>
                  {p.monthly ? <p className="mt-1 text-sm font-bold text-primary">${p.monthly}/mes</p> : null}
                  {p.yearly ? <p className="text-xs text-slate-500">${p.yearly}/año</p> : null}
                  <div className="mt-2 flex flex-col gap-1">
                    <button type="button" onClick={() => selectPackage(p.size, "monthly")} className="rounded-sm bg-primary px-2 py-1 text-xs font-bold text-white hover:bg-orange-600" data-testid={`loc-select-monthly-${p.size}`}>Mensual</button>
                    <button type="button" onClick={() => selectPackage(p.size, "yearly")} className="rounded-sm border border-primary px-2 py-1 text-xs font-bold text-primary hover:bg-primary/5" data-testid={`loc-select-yearly-${p.size}`}>Anual</button>
                  </div>
                </div>
              ))}
              {pkgs.length === 0 && <p className="col-span-full text-sm text-slate-400">Aún no hay paquetes configurados. Contacta al administrador.</p>}
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <div className={`flex flex-wrap items-center justify-between gap-2 rounded-sm px-3 py-2 text-sm ${addon.cancel_at_period_end ? "bg-amber-50" : "bg-accent/5"}`} data-testid="loc-active-banner">
              <span className="font-semibold text-secondary">Paquete activo: {addon.size} sucursales · {addon.billing === "yearly" ? "anual" : "mensual"}{addon.price ? ` · $${addon.price}` : ""}</span>
              <div className="flex items-center gap-3">
                <span className="text-slate-500">{locations.length}/{addon.size} usadas</span>
                {addon.cancel_at_period_end ? (
                  <button type="button" onClick={resumeAddon} className="rounded-sm border border-accent px-2 py-1 text-xs font-bold text-accent transition-colors hover:bg-accent/10" data-testid="loc-resume-btn">Reactivar</button>
                ) : (
                  <button type="button" onClick={() => setCancelOpen(true)} className="rounded-sm border border-slate-300 px-2 py-1 text-xs font-bold text-slate-500 transition-colors hover:border-destructive hover:text-destructive" data-testid="loc-cancel-btn">Cancelar suscripción</button>
                )}
              </div>
            </div>
            {addon.cancel_at_period_end ? (
              <p className="mt-1 text-xs font-semibold text-destructive" data-testid="loc-cancel-scheduled">Cancelación programada · finaliza el {addon.cancel_at ? new Date(addon.cancel_at).toLocaleDateString("es") : (addon.renews_at ? new Date(addon.renews_at).toLocaleDateString("es") : "el final del ciclo")}. Mantienes acceso hasta esa fecha.</p>
            ) : (
              addon.renews_at && (() => {
                const days = Math.ceil((new Date(addon.renews_at) - new Date()) / 86400000);
                return (
                  <p className="mt-1 text-xs text-slate-500" data-testid="loc-renews">
                    Próxima renovación: {new Date(addon.renews_at).toLocaleDateString("es")}
                    {days >= 0 && <span className="ml-1 font-semibold text-primary" data-testid="loc-renews-countdown">· {days === 0 ? "renueva hoy" : days === 1 ? "falta 1 día" : `faltan ${days} días`}</span>}
                  </p>
                );
              })()
            )}
            <div className="mt-4 space-y-3">
              {locations.map((l, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 rounded-sm border border-slate-200 p-3 sm:grid-cols-7" data-testid={`loc-row-${i}`}>
                  <Input value={l.name || ""} onChange={(e) => updateBranch(i, "name", e.target.value)} placeholder="Nombre (ej. Centro)" className="rounded-sm sm:col-span-2" data-testid={`loc-name-${i}`} />
                  <Input value={l.address || ""} onChange={(e) => updateBranch(i, "address", e.target.value)} placeholder="Dirección" className="rounded-sm sm:col-span-2" data-testid={`loc-address-${i}`} />
                  <Input value={l.city || ""} onChange={(e) => updateBranch(i, "city", e.target.value)} placeholder="Ciudad" className="rounded-sm" data-testid={`loc-city-${i}`} />
                  <Input value={l.phone || ""} onChange={(e) => updateBranch(i, "phone", e.target.value)} placeholder="Teléfono" className="rounded-sm" data-testid={`loc-phone-${i}`} />
                  <div className="flex gap-2">
                    <Input value={l.state || ""} onChange={(e) => updateBranch(i, "state", e.target.value)} placeholder="Estado" className="rounded-sm" data-testid={`loc-state-${i}`} />
                    <button type="button" onClick={() => removeBranch(i)} className="shrink-0 rounded-sm border border-slate-200 px-2 text-slate-400 hover:border-destructive hover:text-destructive" data-testid={`loc-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              {locations.length === 0 && <p className="text-sm text-slate-400">Aún no has agregado sucursales.</p>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={addBranch} disabled={locations.length >= addon.size} className="rounded-sm" data-testid="loc-add-btn"><Plus className="mr-1 h-4 w-4" /> Agregar sucursal</Button>
              <Button type="button" onClick={saveBranches} disabled={savingLoc} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="loc-save-btn">{savingLoc ? "…" : "Guardar sucursales"}</Button>
            </div>
          </div>
        )}
      </div>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-addon-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar Multi-Sucursal?</AlertDialogTitle>
            <AlertDialogDescription>
              {addon && addon.stripe_subscription_id
                ? `Programaremos la cancelación para el final de tu ciclo ya pagado${(addon.cancel_at || addon.renews_at) ? ` (${new Date(addon.cancel_at || addon.renews_at).toLocaleDateString("es")})` : ""}. Mantendrás el complemento y tus sucursales activos hasta esa fecha y no habrá ningún cobro adicional. Podrás reactivarlo antes de que termine.`
                : "Se desactivará tu complemento gratuito y se eliminarán tus sucursales. Puedes reactivarlo cuando quieras."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-addon-dismiss">Volver</AlertDialogCancel>
            <AlertDialogAction onClick={cancelAddon} className="bg-destructive hover:bg-destructive/90" data-testid="cancel-addon-confirm">{addon && addon.stripe_subscription_id ? "Sí, cancelar al final del ciclo" : "Sí, cancelar"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
